import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input, Radio, Select, SidePanel } from '@mysaiva/core'
import { ENVIRONMENTS, EXPIRATION_OPTIONS } from '../../api-keys/scopeCatalog'
import { classifyApiKeyError } from '../../api-keys/errors'
import type {
  ApiKeyEnvironment,
  CreateApiKeyPayload,
  ExpirationOption
} from '../../api-keys/apiKeys.types'
import { ScopePicker } from './ScopePicker'
import { FormErrorBanner } from './FormErrorBanner'
import styles from './ApiKeys.module.scss'

interface CreateApiKeyPanelProps {
  open: boolean
  onClose: () => void
  onSubmit: (payload: CreateApiKeyPayload) => void
  submitting?: boolean
  /**
   * Error of the last submit, if it failed; the form stays editable so the
   * user can retry. A duplicate-name rejection is shown on the name field.
   */
  error?: unknown
}

const DEFAULT_ENVIRONMENT: ApiKeyEnvironment = 'sandbox'
const DEFAULT_EXPIRATION: ExpirationOption = '3m'

export function CreateApiKeyPanel({
  open,
  onClose,
  onSubmit,
  submitting,
  error
}: CreateApiKeyPanelProps) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [environment, setEnvironment] = useState<ApiKeyEnvironment>(DEFAULT_ENVIRONMENT)
  const [expiration, setExpiration] = useState<ExpirationOption>(DEFAULT_EXPIRATION)
  const [scopes, setScopes] = useState<string[]>([])
  const [touched, setTouched] = useState(false)
  /** Name of the last rejected submit, so a duplicate-name error clears once it is edited. */
  const [rejectedName, setRejectedName] = useState<string | null>(null)

  // The panel stays mounted while closed, so every opening starts from the
  // defaults instead of the previous attempt (SAIV-11600).
  useEffect(() => {
    if (!open) return
    setName('')
    setEnvironment(DEFAULT_ENVIRONMENT)
    setExpiration(DEFAULT_EXPIRATION)
    setScopes([])
    setTouched(false)
    setRejectedName(null)
  }, [open])

  const failure = error ? classifyApiKeyError(error) : null
  const nameTaken = failure === 'nameExists' && name.trim() === rejectedName

  let nameError: string | undefined
  if (touched && !name.trim()) nameError = t('developerPlatform.apiKeys.form.nameRequired')
  else if (nameTaken) nameError = t('developerPlatform.apiKeys.form.nameExists')
  const scopesError =
    touched && scopes.length === 0 ? t('developerPlatform.apiKeys.form.scopesRequired') : undefined

  const submit = () => {
    setTouched(true)
    if (!name.trim() || scopes.length === 0) return
    setRejectedName(name.trim())
    onSubmit({ name: name.trim(), environment, expiration, scopes })
  }

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title={t('developerPlatform.apiKeys.create.title')}
      description={t('developerPlatform.apiKeys.create.description')}
      actions={{
        cancel: { label: t('developerPlatform.apiKeys.actions.cancel'), onClick: onClose },
        primary: {
          label: t('developerPlatform.apiKeys.create.submit'),
          onClick: submit,
          loading: submitting,
          'data-testid': 'create-key-submit'
        }
      }}
      className={styles.panelTokens}
      mask={false}
      data-testid="create-key-panel"
    >
      <div className={styles.form}>
        {failure === 'nameExists' && (
          <FormErrorBanner
            title={t('developerPlatform.apiKeys.create.nameExistsTitle')}
            text={t('developerPlatform.apiKeys.create.nameExistsText', {
              name: rejectedName
            })}
            trigger={error}
            data-testid="create-key-error"
            data-failure="nameExists"
          />
        )}
        {failure === 'unknown' && (
          <FormErrorBanner
            title={t('developerPlatform.apiKeys.create.errorTitle')}
            text={t('developerPlatform.apiKeys.create.errorText')}
            trigger={error}
            data-testid="create-key-error"
            data-failure="unknown"
          />
        )}
        <div className={styles.field}>
          <label htmlFor="dp-key-name">{t('developerPlatform.apiKeys.form.keyName')}</label>
          <Input
            id="dp-key-name"
            value={name}
            onChange={event => setName(event.target.value)}
            placeholder={t('developerPlatform.apiKeys.form.keyNamePlaceholder')}
            status={nameError ? 'error' : undefined}
            maxLength={80}
            shadow={false}
            data-testid="create-key-name"
          />
          {nameError && (
            <span className={styles.fieldError} role="alert">
              {nameError}
            </span>
          )}
        </div>
        <div className={styles.fieldRow}>
          <div className={styles.field}>
            <span className={styles.groupLabel}>
              {t('developerPlatform.apiKeys.form.environment')}
            </span>
            <Radio.Group
              className={styles.radioRow}
              value={environment}
              onChange={event => setEnvironment(event.target.value as ApiKeyEnvironment)}
              data-testid="create-key-environment"
            >
              {ENVIRONMENTS.map(env => (
                <Radio key={env} value={env} data-testid={`create-key-env-${env}`}>
                  {t(`developerPlatform.apiKeys.environment.${env}`)}
                </Radio>
              ))}
            </Radio.Group>
          </div>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>
              {t('developerPlatform.apiKeys.form.expiration')}
            </span>
            <Select
              value={expiration}
              onChange={value => setExpiration(value as ExpirationOption)}
              options={EXPIRATION_OPTIONS.map(option => ({
                value: option,
                label: t(`developerPlatform.apiKeys.expiration.${option}`)
              }))}
              data-testid="create-key-expiration"
            />
          </div>
        </div>
        <div className={styles.divider} />
        <ScopePicker selected={scopes} onChange={setScopes} error={scopesError} />
      </div>
    </SidePanel>
  )
}
